import { useMemo, useRef, useState, type RefObject } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, RefreshCw, Shuffle, Sparkles } from 'lucide-react';
import {
  DEFAULT_USER_SETTINGS,
  STUDY_MODES,
  STUDY_MODE_LABELS,
  type StudyMode,
} from '@en3/shared';
import { analysisApi } from '@/api/analysis.api';
import { queryKeys } from '@/api/client';
import { contentApi } from '@/api/content.api';
import { toDisplayMessage } from '@/api/errors';
import { useAuthUser } from '@/api/hooks/useAuth';
import { ROUTES } from '@/app/routes';
import { AdvicePanel } from '@/components/advice/AdvicePanel';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '@/components/common/States';
import { Badge, Card, CardBody, CardHeader, CardTitle, Select } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { cn } from '@/lib/cn';

/**
 * 学习建议（阶段 7）。
 *
 * 页面的问题定义是「这一篇文章，用哪种方法学」。因此输入只有两个：文章 + 模式。
 * 两者都放在页面上方，结果在下方，顺序不能颠倒 —— 先给结论再让用户选参数，
 * 会出现"结论是上一篇文章的"这种混乱。
 *
 * 三个交互决定：
 *
 * 1. **模式用一排按钮而不是下拉**。五种模式的差异是这一页的核心概念（精读 ≠ 泛读），
 *    下拉会把它们藏起来，用户看不到"还有别的选法"。按钮上额外给一句人话解释
 *    （"限时读大意，不查词不回头"），比只写"泛读"有用得多。
 *
 * 2. **选中的文章写进 URL（`?articleId=`）**。这样建议可以被分享、被收藏、被回访，
 *    也支持从阅读页/目录页直接跳进来。进入页面时先读 `?articleId=` 作为初始选择。
 *
 * 3. **「换一篇 / 换个模式」是滚动定位而不是弹窗**。选择器本来就在同一页上，
 *    再开一层弹窗只是把同一份控件渲染两次；滚过去并聚焦更直接，也不会产生两份状态。
 *
 * 默认模式取账号里的 `settings.defaultStudyMode`（个人中心可改）。注意它可能在登录态
 * 恢复完成之后才到达，所以用一个"用户是否手动改过"的标记来避免覆盖用户的点击。
 */

/**
 * 五种模式各自的一句话说明。
 *
 * 为什么必须写：模式按钮上只有"精读/泛读"这样的两个字，用户（尤其是刚上手的学生）
 * 未必分得清它们**做法上的差别**。这句话就是选模式的依据，也是这一页的核心价值 ——
 * 建议之所以有用，是因为"同样一篇文章，换一种方式读"会得到完全不同的训练效果。
 */
const MODE_HINTS: Record<StudyMode, string> = {
  intensive: '逐句拆结构、查词、做笔记，一篇读透',
  extensive: '限时读大意，不查词不回头，靠量取胜',
  shadowing: '跟读音频，模仿语音语调与断句',
  backtranslation: '看中文回译成英文，再与原句对比',
  retell: '读完用英文复述，练输出与组织能力',
};

/** 学习建议页：选文章 + 选模式 → 拿到可执行的学习计划。 */
export function StudyAdvicePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthUser();

  const [articleId, setArticleId] = useState(() => searchParams.get('articleId') ?? '');
  /**
   * 模式选择是「默认值 + 用户覆盖」的派生结构，**不用 effect 去同步**：
   * 账号里的 `defaultStudyMode` 可能在登录态恢复之后才到达，如果把 state 初值当成真相、
   * 再用 effect 覆盖它，就会出现"用户刚点了泛读、登录态一到又被改回精读"的抢状态问题。
   * 这里让 state 只表示"用户显式选过什么"，没选过就跟着账号默认值走。
   */
  const [modeOverride, setModeOverride] = useState<StudyMode | null>(null);
  const defaultMode = user?.settings.defaultStudyMode ?? DEFAULT_USER_SETTINGS.defaultStudyMode;
  const mode = modeOverride ?? defaultMode;

  const articleFieldRef = useRef<HTMLDivElement | null>(null);
  const modeFieldRef = useRef<HTMLDivElement | null>(null);

  const articles = useQuery({
    // 与长难句页共用同一个键：同一份"全部文章"数据没必要缓存两份
    queryKey: queryKeys.articles({ scope: 'all', pageSize: 50 }),
    queryFn: () => contentApi.articles({ scope: 'all', pageSize: 50 }),
  });

  const advice = useQuery({
    queryKey: queryKeys.advice(articleId, mode),
    queryFn: () => analysisApi.advice(articleId, { mode }),
    // 没选文章就不请求：`/learning-advice/:id` 需要真实 id，空请求只会拿到 404
    enabled: articleId.length > 0,
  });

  // 用 useMemo 固定数组引用：`articles.data?.items ?? []` 每次渲染都会产生新数组，
  // 直接作为 useMemo 的依赖会让下面的 `selected` 每轮都重算（exhaustive-deps 也会提醒）
  const articleOptions = useMemo(() => articles.data?.items ?? [], [articles.data]);
  const selected = useMemo(
    () => articleOptions.find((article) => article.id === articleId) ?? null,
    [articleOptions, articleId],
  );

  function selectArticle(next: string) {
    setArticleId(next);
    // 写进 URL（replace 而不是 push）：避免在浏览器历史里留下每选一次就多一条记录
    const params = new URLSearchParams(searchParams);
    if (next.length > 0) params.set('articleId', next);
    else params.delete('articleId');
    setSearchParams(params, { replace: true });
  }

  function selectMode(next: StudyMode) {
    // 只记录"用户选过什么"，默认值仍然跟着账号设置走（见上面 modeOverride 的说明）
    setModeOverride(next);
  }

  function scrollTo(ref: RefObject<HTMLDivElement | null>) {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  return (
    <section className="mx-auto max-w-3xl px-5 py-8">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Sparkles aria-hidden className="size-6 text-primary" />
          学习建议
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          同一个「精读」和「泛读」的差别，比读同一篇文章十遍更大。选一篇文章 + 一种方法，
          这里会给出带时长预算的执行步骤、该重点处理的词与句。
        </p>
      </header>

      {/* ------------------------------------------------------------------ */}
      {/* 选择器                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Card className="mt-5">
        <CardHeader>
          <CardTitle>选择文章与方法</CardTitle>
          <div className="flex flex-wrap gap-1.5">
            <Button variant="ghost" size="sm" onClick={() => scrollTo(articleFieldRef)}>
              <RefreshCw aria-hidden className="size-3.5" />
              换一篇
            </Button>
            <Button variant="ghost" size="sm" onClick={() => scrollTo(modeFieldRef)}>
              <Shuffle aria-hidden className="size-3.5" />
              换个模式
            </Button>
          </div>
        </CardHeader>

        <CardBody className="space-y-4">
          <div ref={articleFieldRef}>
            <Field
              id="advice-article"
              label="文章"
              hint={
                articles.isError
                  ? '文章列表加载失败，可先去教材目录选一篇再回到这里。'
                  : '列出公共库与你自己导入的文章。'
              }
              error={articles.data && articleOptions.length === 0 ? '还没有可用的文章，先去教材目录看看。' : undefined}
            >
              <Select
                id="advice-article"
                value={articleId}
                className="w-full"
                disabled={articles.isPending}
                onChange={(event) => selectArticle(event.target.value)}
              >
                <option value="">{articles.isPending ? '正在加载文章…' : '请选择一篇文章'}</option>
                {articleOptions.map((article) => (
                  <option key={article.id} value={article.id}>
                    {article.titleZh ?? article.title}
                    {article.difficultyScore === null ? '' : `（难度 ${Math.round(article.difficultyScore)}）`}
                    {article.cefrEstimate ? ` · ${article.cefrEstimate}` : ''}
                  </option>
                ))}
              </Select>
            </Field>

            {selected && (
              <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <BookOpen aria-hidden className="size-3.5" />
                <Link to={ROUTES.reader(selected.id)} className="text-primary underline underline-offset-4">
                  打开原文
                </Link>
                <span>·</span>
                <span>{selected.wordCount} 词</span>
                <span>·</span>
                <span>{selected.sentenceCount} 句</span>
                {selected.longSentenceCount > 0 && <Badge tone="warning">{selected.longSentenceCount} 个长难句</Badge>}
              </p>
            )}
          </div>

          <div ref={modeFieldRef}>
            <p className="text-sm font-medium">学习模式</p>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {STUDY_MODES.map((item) => {
                const active = item === mode;
                return (
                  <button
                    key={item}
                    type="button"
                    aria-pressed={active}
                    onClick={() => selectMode(item)}
                    className={cn(
                      'rounded-lg border px-3 py-2 text-left transition-colors',
                      active ? 'border-primary bg-accent text-accent-foreground' : 'hover:bg-accent/40',
                    )}
                  >
                    <span className="flex items-center gap-2 text-sm font-medium">
                      {STUDY_MODE_LABELS[item]}
                      {active && <span className="text-[10px] text-primary">已选</span>}
                    </span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{MODE_HINTS[item]}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* 结果                                                               */}
      {/* ------------------------------------------------------------------ */}
      <div className="mt-5">
        {articleId.length === 0 ? (
          <EmptyBlock
            title="先选一篇文章"
            description="建议是按「文章 + 方法」给出的：同一篇文章用精读和泛读，要做的事完全不同。"
            action={
              <Link
                to={ROUTES.textbooks}
                className="inline-flex h-9 items-center rounded-md border px-3 text-sm hover:bg-accent hover:text-accent-foreground"
              >
                去教材目录挑一篇
              </Link>
            }
          />
        ) : advice.isPending ? (
          <LoadingBlock label="正在生成学习建议…" />
        ) : advice.isError ? (
          <ErrorBlock message={toDisplayMessage(advice.error)} onRetry={() => void advice.refetch()} />
        ) : (
          <AdvicePanel result={advice.data} />
        )}
      </div>
    </section>
  );
}
